import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PrintIcon from '@mui/icons-material/Print';
import CircularProgress from '@mui/material/CircularProgress';
import '../Customer/Customer.css';

function CashBook() {
    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const { isAuthenticated, companyId, permissions, token, logout } = useAuth();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
    }, [isAuthenticated, companyId, token, logout]);

    useEffect(() => {
        if (fromDate && toDate) {
            fetchTableData();
        }
    }, [fromDate, toDate]);

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const today = new Date();
        let year = today.getFullYear();
        if (today.getMonth() + 1 > 3) {
            return `${year}-04-01`;
        } else {
            return `${year - 1}-04-01`;
        }
    };

    const hasReadReportPermission = permissions.includes('Read Reports');

    const fetchTableData = async () => {
        if (hasReadReportPermission) {
            setDataItem([]);
            setLoading(true);
            try {
                const response = await fetchData('ReportApi', `/CashBook/${companyId}/0/${fromDate}/${toDate}`, token);
                if (response.responseCode === '1111') {
                    setDataItem(response.response_data);
                }
            } catch (error) {
                console.error('Error fetching CashBook Details:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    const filteredDataItem = dataItem.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = date.toLocaleString('default', { month: 'short' });
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    let bal = 0;
    const computeBalance = (data) => {
        if (data.description1 !== 'Closing Balance' && data.description1 !== 'Total') {
            if (data.debit) {
                bal += Math.abs(data.debit);
            }
            if (data.credit) {
                bal -= Math.abs(data.credit);
            }
        }
        if (data.description1 !== 'Closing Balance' && data.description1 !== 'Total') {
            return bal > 0 ? `${bal.toFixed(2)} Dr` : `${Math.abs(bal).toFixed(2)} Cr`;
        }
        return '';
    };

    const handleExportExcel = () => {
        const excelData = [];
        const headerCaption = [`Cash Book From : ${formatDate(fromDate)} To : ${formatDate(toDate)}`];
        excelData.push(headerCaption);
        const headerRow = ['Date', 'Description', 'Debit', 'Credit', 'Balance'];
        excelData.push(headerRow);

        let excelBal = 0;
        filteredDataItem.forEach((rowData) => {
            const dataRow = [];
            if (rowData.description1 === 'Opening Balance' || rowData.description1 === 'Closing Balance' || rowData.description1 === 'Total') {
                dataRow.push('', rowData.description1, rowData.debit || '', rowData.credit || '', '');
            } else {
                if (rowData.debit) excelBal += Math.abs(rowData.debit);
                if (rowData.credit) excelBal -= Math.abs(rowData.credit);
                const balStr = excelBal > 0 ? `${excelBal.toFixed(2)} Dr` : `${Math.abs(excelBal).toFixed(2)} Cr`;
                dataRow.push(formatDate(rowData.billdate), rowData.description1, rowData.debit || '', rowData.credit || '', balStr);
            }
            excelData.push(dataRow);
        });

        const ws = XLSX.utils.aoa_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'CashBook');
        ws['!cols'] = [{ wch: 15 }, { wch: 35 }, { wch: 15 }, { wch: 15 }, { wch: 15 }];
        XLSX.writeFile(wb, 'CashBook.xlsx');
    };

    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        if (!printWindow) return;

        let printBal = 0;
        const rows = filteredDataItem.map((data) => {
            const isSpecial = data.description1 === 'Opening Balance' || data.description1 === 'Closing Balance' || data.description1 === 'Total';
            let balStr = '';
            if (!isSpecial) {
                if (data.debit) printBal += Math.abs(data.debit);
                if (data.credit) printBal -= Math.abs(data.credit);
                balStr = printBal > 0 ? `${printBal.toFixed(2)} Dr` : `${Math.abs(printBal).toFixed(2)} Cr`;
            }
            return `
                <tr class="${isSpecial ? 'special-row' : ''}">
                    <td>${isSpecial ? '' : formatDate(data.billdate)}</td>
                    <td>${data.description1}</td>
                    <td class="amount">${data.debit != null ? '₹' + data.debit.toFixed(2) : ''}</td>
                    <td class="amount">${data.credit != null ? '₹' + data.credit.toFixed(2) : ''}</td>
                    <td class="amount">${balStr}</td>
                </tr>
            `;
        }).join('');

        printWindow.document.write(`
            <html>
            <head>
                <title>Cash Book</title>
                <style>
                    body { font-family: Arial, sans-serif; padding: 20px; }
                    h2 { color: #1e3a5f; margin-bottom: 20px; }
                    table { border-collapse: collapse; width: 100%; }
                    th { background: #1e3a5f; color: white; padding: 10px; text-align: left; font-size: 12px; }
                    td { border: 1px solid #e2e8f0; padding: 8px; font-size: 12px; }
                    tr:nth-child(even) { background: #f8fafc; }
                    .special-row { background: #e0f2fe !important; font-weight: bold; }
                    .amount { text-align: right; }
                </style>
            </head>
            <body>
                <h2>Cash Book</h2>
                <p>From: ${formatDate(fromDate)} To: ${formatDate(toDate)}</p>
                <table>
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Description</th>
                            <th class="amount">Debit</th>
                            <th class="amount">Credit</th>
                            <th class="amount">Balance</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${rows}
                    </tbody>
                </table>
            </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.print();
    };

    return (
        <div className="customer-container">
            {loading && (
                <div className="loading-overlay">
                    <CircularProgress size={40} className="loading-spinner" />
                    <span className="loading-text">Loading...</span>
                </div>
            )}

            {/* Header */}
            <div className="page-header" style={{ flexWrap: 'wrap' }}>
                <h1 className="page-title">Cash Book</h1>
                <div className="header-actions" style={{ flexWrap: 'wrap', gap: '10px' }}>
                    <div className="search-box">
                        <SearchIcon />
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <label style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>From</label>
                            <input
                                className="form-input"
                                type="date"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                style={{ width: '130px', padding: '6px 8px', fontSize: '13px' }}
                            />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <label style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>To</label>
                            <input
                                className="form-input"
                                type="date"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                style={{ width: '130px', padding: '6px 8px', fontSize: '13px' }}
                            />
                        </div>
                    </div>
                    <button
                        onClick={fetchTableData}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '8px 14px',
                            background: '#1e3a5f',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '500'
                        }}
                    >
                        <RefreshIcon fontSize="small" />
                        Refresh
                    </button>
                    <button
                        onClick={handleExportExcel}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '8px 14px',
                            background: '#059669',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '500'
                        }}
                    >
                        <FileDownloadIcon fontSize="small" />
                        Excel
                    </button>
                    <button
                        onClick={handlePrint}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '8px 14px',
                            background: '#7c3aed',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '500'
                        }}
                    >
                        <PrintIcon fontSize="small" />
                        Print
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="table-container">
                <div className="table-wrapper">
                    <table className="customer-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Description</th>
                                <th style={{ textAlign: 'right' }}>Debit</th>
                                <th style={{ textAlign: 'right' }}>Credit</th>
                                <th style={{ textAlign: 'right' }}>Balance</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredDataItem.length > 0 ? (
                                filteredDataItem.map((data, index) => {
                                    const isSpecial = data.description1 === 'Opening Balance' || data.description1 === 'Closing Balance' || data.description1 === 'Total';
                                    const balance = computeBalance(data);
                                    return (
                                        <tr key={index} style={{ background: isSpecial ? '#e0f2fe' : '' }}>
                                            <td className="cell-date">{isSpecial ? '' : formatDate(data.billdate)}</td>
                                            <td style={{ fontWeight: isSpecial ? '600' : '400' }}>{data.description1}</td>
                                            <td style={{ textAlign: 'right', fontWeight: isSpecial ? '600' : '400', color: data.debit > 0 ? '#dc2626' : '' }}>
                                                {data.debit != null ? `₹ ${data.debit.toFixed(2)}` : ''}
                                            </td>
                                            <td style={{ textAlign: 'right', fontWeight: isSpecial ? '600' : '400', color: data.credit > 0 ? '#059669' : '' }}>
                                                {data.credit != null ? `₹ ${data.credit.toFixed(2)}` : ''}
                                            </td>
                                            <td style={{ textAlign: 'right', fontWeight: '600' }}>
                                                {balance && (
                                                    <span style={{
                                                        background: balance.includes('Dr') ? '#fee2e2' : '#d1fae5',
                                                        color: balance.includes('Dr') ? '#dc2626' : '#059669',
                                                        padding: '4px 8px',
                                                        borderRadius: '4px',
                                                        fontSize: '12px'
                                                    }}>
                                                        {balance}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="5">
                                        <div className="empty-state">
                                            <p>No cash book entries found</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default CashBook;
